import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import Moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { CollectionsApi } from "../../services/api/api";
import { confirm } from "../confirmation";
import { UPLOADS_BUCKET } from "../../config";
import { S3Client, GetObjectCommand, ListObjectsV2Command } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { getContrastYIQ, neutralTierBg } from "../../utils/colorUtils";

Moment.locale("en");

function CollectionListItemV2(props) {
  const theme = useTheme();
  const { data, openModal, AWS, userId, accountType } = props;
  const [image, setImage] = useState("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);
  const isMounted = useRef(true);
  const dragInitiated = useRef(false);

  const handleRenameSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = (renameValue || "").trim();
    if (!trimmed) {
      toast.warn("Name cannot be empty");
      return;
    }
    if (!data.collectionId) {
      toast.error("Cannot rename: missing collectionId");
      return;
    }
    setRenameLoading(true);
    try {
      const token = props.token;
      const t = typeof token === 'function' ? await token() : token;
      const result = await CollectionsApi.updateCollectionName(t, data.collectionId, trimmed);
      if (result && result.success !== false) {
        toast.success("Collection renamed");
        setRenameOpen(false);
        if (typeof props.onRenamed === 'function') {
          props.onRenamed();
        }
      } else {
        toast.error("Rename failed: " + ((result && result.message) || "Unknown error"));
      }
    } catch (err) {
      toast.error("Rename failed: " + (err && err.message ? err.message : String(err)));
    }
    setRenameLoading(false);
  };

  const handleDelete = () => {
    if (!data.collectionId) {
      toast.error("Cannot delete: missing collectionId");
      return;
    }
    confirm(
      "Are you sure you want to delete this collection? This cannot be undone.",
      "Delete",
      "Cancel"
    ).then(async (ok) => {
      if (!ok) return;
      try {
        const token = props.token;
        const t = typeof token === 'function' ? await token() : token;
        const result = await CollectionsApi.deleteCollection(t, data.collectionId);
        if (result && result.success !== false) {
          toast.success("Collection deleted");
          if (typeof props.onDeleted === 'function') {
            props.onDeleted();
          }
        } else {
          toast.error("Delete failed: " + ((result && result.message) || "Unknown error"));
        }
      } catch (err) {
        toast.error("Delete failed: " + (err && err.message ? err.message : String(err)));
      }
    }, () => {});
  };

  // Debug helper
  const debugLog = (message, details = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${data.title}] ${message}`, details);
    }
  };

  // Clear cache for this specific image key (useful for debugging)
  const clearImageCache = (key) => {
    if (key) {
      localStorage.removeItem(key);
      debugLog(`Cleared cache for key: ${key}`);
    }
  };

  // Debug function - expose globally for testing (dev only)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      window[`clearCache_${data.relId}`] = () => {
        if (data.previewImage) {
          clearImageCache(data.previewImage);
        }
        setImage("");
        setImageLoading(true);
        loadAppropriateImage();
      };
    }
  }, [data.relId, data.previewImage]);

  useEffect(() => {
    isMounted.current = true;
    // Always reset image and error state when collection changes
    setImage("");
    setImageError(false);
    setImageLoading(true);
    loadAppropriateImage();
    return () => {
      isMounted.current = false;
    };
  }, [data.previewImage, data.collectionId, userId, AWS]);

  const loadAppropriateImage = async () => {
    debugLog("loadAppropriateImage called", { 
      hasImage: !!image, 
      previewImage: data.previewImage,
      imageLoading 
    });
    setImageLoading(true);
    setImageError(false);
    // 1. Check for previewImage attribute first (highest priority)
    if (data.previewImage && data.previewImage.trim() !== "") {
      debugLog(`Loading previewImage: ${data.previewImage}`);
      await getImageFromKey(data.previewImage);
    }
    // 2. If no previewImage, get first image from optimized folder
    else if (data.collectionId && userId) {
      debugLog("No previewImage found, loading first optimized image");
      const ownerId = data.userId || userId; // linked collections use owner's userId
      await getFirstOptimizedImage(data.collectionId, ownerId);
    }
    else {
      debugLog("No image available for collection");
      setImageLoading(false);
      setImageError(true);
    }
  };

  const getImageFromKey = async (key, last = false, remake = false) => {
    // Validate that key exists and is not empty
    if (!key || key.trim() === "") {
      console.warn("Invalid or empty key provided to getImageFromKey:", key);
      setImageError(true);
      setImageLoading(false);
      return;
    }

    if (isMounted.current && AWS?.config?.credentials) {
      const s3 = new S3Client({
        region: "us-east-1",
        credentials: AWS.config.credentials,
      });
      const time = +new Date();
      let store = localStorage.getItem(key);

      // Check if this is a different previewImage than what we had cached
      if (data.previewImage && key === data.previewImage && image && !image.includes(encodeURIComponent(key))) {
        debugLog("PreviewImage changed, clearing cache");
        clearImageCache(key);
        store = null;
        remake = true;
      }

      const getUrl = async () => {
        try {
          debugLog(`Generating new signed URL for key: ${key}`);
          const command = new GetObjectCommand({
            Bucket: UPLOADS_BUCKET,
            Key: key,
          });
          const url = await getSignedUrl(s3, command, { expiresIn: 21600 }); // 6 hours
          localStorage.setItem(key, JSON.stringify({ url: url, time: time }));
          if (isMounted.current) {
            debugLog(`Setting new image URL: ${url.substring(0, 50)}...`);
            setImage(url);
            setImageLoading(false);
            setImageError(false);
          }
        } catch (err) {
          debugLog("Error loading image", { error: err.message });
          if (isMounted.current) {
            setImageLoading(false);
            setImageError(true);
          }
          if (!last) {
            // Retry once, then give up
            getImageFromKey(key, true);
          } else {
            // If this was a previewImage that failed, try fallback to first optimized image
            if (data.collectionId && data.userId && key === data.previewImage) {
              debugLog("PreviewImage failed, trying first optimized image");
              getFirstOptimizedImage(data.collectionId, data.userId);
            }
          }
        }
      };

      if (store === null || remake) {
        await getUrl();
      } else {
        try {
          let storedData = JSON.parse(store);
          const currentTime = +new Date();
          const cachedTime = parseInt(storedData.time);
          const timeDiff = currentTime - cachedTime;
          // Check if stored URL is still valid (6 hours = 21600000 milliseconds for more frequent refresh)
          if (timeDiff < 21600000 && storedData.url) {
            // Validate the cached image by attempting to load it
            const testImage = new window.Image();
            testImage.onload = () => {
              if (isMounted.current) {
                debugLog(`Using cached image (age: ${Math.round(timeDiff / 1000 / 60)} minutes)`);
                setImage(storedData.url);
                setImageLoading(false);
                setImageError(false);
              }
            };
            testImage.onerror = () => {
              debugLog("Cached image URL failed to load, clearing cache and refreshing");
              clearImageCache(key);
              getUrl();
            };
            testImage.src = storedData.url;
            return;
          } else {
            debugLog(`Cached image expired (age: ${Math.round(timeDiff / 1000 / 60)} minutes), refreshing`);
          }
          await getUrl();
        } catch (parseError) {
          console.warn("Error parsing stored image data, refreshing:", parseError);
          await getUrl();
        }
      }
    }
  };

  const getFirstOptimizedImage = async (setId, userId) => {
    if (!isMounted.current || !AWS?.config?.credentials) return;

    try {
      const s3 = new S3Client({
        region: "us-east-1",
        credentials: AWS.config.credentials,
      });

      // Try to list objects in the optimized folder first
      const optimizedPath = `collections/${data.userId}/${setId}/optimized/`;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Searching for optimized images in: ${optimizedPath}`);
      }

      const listCommand = new ListObjectsV2Command({
        Bucket: UPLOADS_BUCKET,
        Prefix: optimizedPath,
        MaxKeys: 1
      });

      const response = await s3.send(listCommand);

      if (response.Contents && response.Contents.length > 0) {
        const firstImageKey = response.Contents[0].Key;
        if (process.env.NODE_ENV === 'development') {
          console.log(`Found optimized image for ${data.title}: ${firstImageKey}`);
        }
        await getImageFromKey(firstImageKey);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log(`No optimized images found for collection ${data.title} (${setId})`);
        }
        setImageLoading(false);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Error getting images for collection ${data.title}:`, err);
      }
      if (isMounted.current) {
        setImageLoading(false);
      }
    }
  };

  // Linked collections navigate by setId (UUID). Own collections navigate by relId.
  const idForRoute = data.isLinked ? data.setId : data.relId;
  const redirect = idForRoute ? `/collections/${idForRoute}` : (data.collectionId ? `/collections/${data.collectionId}` : null);
  const tierName = data.tierName || "No Tier";
  const isUploader = accountType === "uploader";

  if (!redirect) return null;

  return (
    <Box
      component={RouterLink}
      to={redirect}
      draggable
      onDragStart={(e) => {
        if (!dragInitiated.current) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData('text/plain', JSON.stringify({
          collectionId: data.collectionId || data.relId,
          title: data.title
        }));
        e.dataTransfer.effectAllowed = 'move';
        e.target.style.opacity = '0.5';
        setTimeout(() => { dragInitiated.current = false; }, 0);
      }}
      onDragEnd={(e) => {
        e.target.style.opacity = '1';
        dragInitiated.current = false;
      }}
      onClick={(e) => {
        // Let the browser handle the link navigation natively
        // Only stop propagation for action buttons
      }}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        height: '100%',
      }}
    >
    <Card
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
      <Box>
        {imageLoading ? (
          <Box
            sx={{
              height: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : image && !imageError ? (
          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="div"
              sx={{
                height: 150,
                backgroundImage: `url(${image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            {/* Debug indicator - shows if using previewImage or fallback */}
            {process.env.NODE_ENV === 'development' && (
              <Chip
                label={data.previewImage ? "PREVIEW" : "FALLBACK"}
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  fontSize: "10px",
                  height: "16px",
                  backgroundColor: data.previewImage ? "#4caf50" : "#ff9800",
                  color: "white",
                }}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No image available
            </Typography>
          </Box>
        )}
      </Box>
      <CardContent
        sx={{ flexGrow: 1 }}
      >
        <Typography gutterBottom variant="h6" component="div">
          {data.title}
        </Typography>
        <Chip
          label={tierName}
          size="small"
          sx={{
            backgroundColor: data.tierColor || neutralTierBg(theme.palette.mode),
            color: getContrastYIQ(data.tierColor || neutralTierBg(theme.palette.mode)),
            mb: 1,
          }}
        />
        {data.isLinked === true && (
          <Chip
            label={`Linked: ${data.linkType || "shared"}`}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ mb: 1, ml: 0.5 }}
          />
        )}
        {data.folderPath && Array.isArray(data.folderPath) && data.folderPath.length > 0 && (
          <Chip
            label={data.folderPath.join(' / ')}
            size="small"
            variant="outlined"
            sx={{
              ml: 0.5,
              mb: 1,
              fontSize: '0.7rem',
              height: '20px',
            }}
          />
        )}
      </CardContent>
      {/* Created date positioned in bottom left corner */}
      <Typography 
        variant="caption" 
        color="text.secondary"
        sx={{
          position: "absolute",
          bottom: 8,
          left: 8,
          fontSize: "0.7rem",
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '2px 6px',
          borderRadius: '4px',
          backdropFilter: 'blur(4px)'
        }}
      >
        Created {Moment.unix(data.date).format("LL") === "Invalid date"
          ? data.date
          : Moment.unix(data.date).format("MMM DD, YYYY")}
      </Typography>
      </Box>
      <Box
        className="ss-drag-handle"
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2,
          cursor: "grab",
          display: { xs: "none", sm: "flex" },
          color: "text.secondary",
          opacity: 0.5,
          '&:hover': { opacity: 1 },
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: 1,
          p: 0.25,
        }}
        aria-label="Drag to move collection"
        onMouseDown={(e) => {
          e.stopPropagation();
          dragInitiated.current = true;
          const startX = e.clientX;
          const startY = e.clientY;
          let moved = false;
          const handleMove = (moveEvt) => {
            if (Math.abs(moveEvt.clientX - startX) > 5 || Math.abs(moveEvt.clientY - startY) > 5) {
              moved = true;
              document.removeEventListener('mousemove', handleMove);
              document.removeEventListener('mouseup', handleUp);
              const dragData = { collectionId: data.collectionId || data.relId, title: data.title };
              props.onDragStart?.(data);
            }
          };
          const handleUp = () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
            if (!moved) {
              dragInitiated.current = false;
            }
          };
          document.addEventListener('mousemove', handleMove);
          document.addEventListener('mouseup', handleUp);
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <CardActions sx={{ justifyContent: "flex-end", position: "absolute", top: 0, right: 0, zIndex: 1 }}>
        {!isUploader && (
          <IconButton
            size="small"
            aria-label="Send collection"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openModal(data.relId, data.title);
            }}
            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}
          >
            <SendIcon />
          </IconButton>
        )}
        <IconButton
          size="small"
          aria-label="More actions"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setAnchorEl(e.currentTarget); }}
          sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </CardActions>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {data.isLinked === true ? (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              props.onRemoveFromLibrary?.(data);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from My Library</ListItemText>
          </MenuItem>
        ) : [
          <MenuItem key="manage" onClick={(e) => { e.stopPropagation(); setAnchorEl(null); props.onManageAccess?.(data); }}>
            <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Manage Access</ListItemText>
          </MenuItem>,
          <MenuItem key="move" onClick={(e) => { e.stopPropagation(); setAnchorEl(null); props.onMove?.(data); }}>
            <ListItemIcon><DriveFileMoveOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Move to folder</ListItemText>
          </MenuItem>,
          <MenuItem key="rename" onClick={(e) => { e.stopPropagation(); setAnchorEl(null); setRenameValue(data.title || ""); setRenameOpen(true); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>,
          <MenuItem key="delete" onClick={(e) => { e.stopPropagation(); setAnchorEl(null); handleDelete(); }}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        ]}
      </Menu>
      <Dialog open={renameOpen} onClose={() => { if (!renameLoading) setRenameOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Rename Collection</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Collection name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            margin="normal"
            disabled={renameLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={renameLoading}>Cancel</Button>
          <Button onClick={handleRenameSubmit} variant="contained" disabled={renameLoading}>
            {renameLoading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
    </Box>
  );
}

export default CollectionListItemV2;
